import { useEffect, useState, useRef } from "react";
import { useAtom } from "jotai";
import { isMapShownAtom } from "../../jotai-store/isMapShow";
import { useToaster } from "../../components/Toaster";
import { isMinimizedAtom } from "../../jotai-store/isMapShow";


const MapModal = ({
  isOpen,
  onClose,
  children,
  handleUpdateBookingObject,
  pickupLocation,
  dropoffLocation,
  isTruckRequired,
  isLocationRouteComputing = false,
}) => {
  const [, setIsMapShown] = useAtom(isMapShownAtom);
  const [minimized, setMinimized] = useAtom(isMinimizedAtom);
  const modalRef = useRef(null);
  const activatedRef = useRef(false);
  const { showToast } = useToaster();




  // Handle escape key press
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape") {
        setMinimized(true); // Escape → minimize true
      } else if (e.key === " ") {
        setMinimized(false); // Space → minimize false
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyPress);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);


  // When modal opens, ensure it's shown (not minimized). Do not auto-minimize on close.
  useEffect(() => {
    if (isOpen) setMinimized(false);
  }, [isOpen]);

  // Listen for clicks anywhere on the document; if click is outside modal, minimize.
  useEffect(() => {
    if (!isOpen) return;
    // Activate click handling after a short delay to avoid catching navigation-initiated clicks
    activatedRef.current = false;
    const activateTimer = setTimeout(() => {
      activatedRef.current = true;
    }, 250);

    const handleDocClick = (e) => {
      if (!activatedRef.current) return;
      if (!modalRef.current) return;
      // Only minimize on real user-initiated clicks (ignore synthetic/programmatic events)
      if (!e.isTrusted) return;
      if (!modalRef.current.contains(e.target)) {
        setMinimized(true);
      }
    };

    document.addEventListener("click", handleDocClick);
    return () => {
      clearTimeout(activateTimer);
      document.removeEventListener("click", handleDocClick);
      activatedRef.current = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed w-full inset-0 z-50 flex items-end justify-center pointer-events-none">
      {/* Backdrop with fade-in animation; clicking backdrop minimizes instead of closing */}
      <div className="absolute inset-0 transition-opacity duration-300 pointer-events-none" />

      {/* Modal Container with slide-up animation */}
      {minimized ? (
        // Minimized header bar
        <div
          className="relative bg-white rounded-t-[20px] w-full max-h-[6rem] shadow-2xl transform transition-transform duration-300 ease-out flex items-center overflow-hidden pointer-events-auto cursor-pointer"
          style={{ zIndex: 9999 }}
          onClick={() => setMinimized(false)}
          ref={modalRef}
        >
          <div className="w-full px-6 py-3 flex items-center justify-center">
            <div className="w-14 h-1.5 bg-[#cfcfe0] rounded-full mx-auto" />
          </div>
        </div>
      ) : (
        <div
          className="relative bg-white rounded-t-[20px] w-full h-[50vh] shadow-2xl transform transition-transform duration-300 ease-out translate-y-0 flex flex-col overflow-hidden pointer-events-auto"
          style={{ zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
          ref={modalRef}
        >
          {/* Header with top handle and close button */}
          <div className="py-3 px-4 relative">
            <div className="w-14 h-1.5 bg-[#cfcfe0] rounded-full mx-auto" />

          </div>




          {/* Modal Content */}
          <div className="pt-6 md:px-[22px] px-[32px] pb-24 w-full md:max-w-[400px] mx-auto overflow-auto flex-1">
            <p class="font-instrument-sans font-semibold text-[24px] leading-[29px] 
       text-[#211F59] mb-[5px]">
              Where are you going?
            </p>
            {children}
          </div>

          {/* Footer buttons: Back (square) + Next (gradient pill) - fixed to modal bottom */}
          <div className="absolute left-0 right-0 bottom-0 pb-3 bg-white pt-2">
            <div className="w-full md:max-w-[400px] mx-auto md:px-[22px] px-[32px]">
              <div className="flex items-center gap-[9.5px]">
                {/* Back button - 25% */}
                <button
                  type="button"
                  onClick={() => {
                    handleUpdateBookingObject(
                      "tripStatus",
                      "visionItems",
                      `BackToItems`
                    );
                    setIsMapShown(false);
                  }}
                  className="w-[25%] h-12 flex items-center justify-center rounded-lg border border-[#7876A1] text-[#5b4f90] shadow-sm"
                  aria-label="Back"
                >
                  <img
                    src="/images/NewUiIcons/Arrow.svg"
                    alt="back"
                    width={18.2}
                    height={17.57}
                  />
                </button>

                {/* Next button - 75% */}
                <button
                  type="button"
                  onClick={() => {
                    if (isLocationRouteComputing) {
                      showToast({
                        type: "warning",
                        message: "Please wait while we calculate route distance and duration.",
                        duration: 4500,
                      });
                      return;
                    }

                    if (pickupLocation.trim() === "" || (isTruckRequired && dropoffLocation.trim() === "")) {
                      showToast({
                        type: "warning",
                        message:
                          "Please enter both pickup and drop-off locations along with their mediums.",
                        duration: 4500,
                      });
                    } else {
                      setIsMapShown(false)

                    }

                  }
                  }
                  disabled={isLocationRouteComputing}
                  className="w-[75%] h-12 rounded-[8px] text-white font-medium bg-[linear-gradient(to_right,#211F59,#4743BF)] shadow-lg flex items-center justify-center gap-3"
                  aria-label="Next"
                >
                  <span>Next</span>
                  <img src="/images/NewUiIcons/arrowicon.svg" alt="arrow icon" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .fixed > .absolute {
          animation: fadeIn 0.3s ease-out;
        }

        .fixed > .relative {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MapModal;
